/**
 * Markdown project → `.zwdoc`, as a pure plan (#259).
 *
 * The shell lists a project's legacy documents (raw text), calls
 * `planProjectConversion`, and hands the plan to the backend, which writes
 * it and commits "Convert documents to .zwdoc". Every document must convert,
 * round-trip and keep every word, or there is no plan: conversion is all or
 * nothing. `npm run zwdoc:convert` (`scripts/zwdoc-convert.mjs`) runs the
 * same per-document checks against a folder.
 */

import { parse as parseYaml } from "yaml";
import { isJust, just, nothing, type Maybe } from "../maybe";
import { err, ok, type Result } from "../result";
import { isAlignMarkerLine } from "./legacy/alignMarkers";
import { stripAllCommentMarkers } from "./legacy/commentMarkers";
import { isIndentMarkerLine } from "./legacy/indentMarkers";
import { isSpacerMarkerLine } from "./legacy/spacerMarkers";
import { removeStoreBlock } from "./legacy/commentStore";
import { parseZwDoc, serializeZwDoc, type ZwDoc } from "./format";
import {
  markdownToZwDoc,
  stripLegacyFrontmatter,
  type LegacyMarkdownDocument,
} from "./fromMarkdown";
import type { ZwDocEntityFields, ZwDocKind } from "./generated/zwDoc";
import { markdownLexer } from "./markdownLexer";
import { projectPlainText } from "./plainText";

const LEGACY_DOCUMENT_RE = /\.(md|markdown|mdown)$/i;

export function isLegacyDocumentPath(path: string): boolean {
  return LEGACY_DOCUMENT_RE.test(path);
}

/** `Chapters/A/scene.md` → `Chapters/A/scene.zwdoc`. */
export function zwdocPathFor(path: string): string {
  return path.replace(LEGACY_DOCUMENT_RE, ".zwdoc");
}

// --- Frontmatter -------------------------------------------------------------

/** Leading YAML block (first one wins), or Nothing. */
function frontmatterYaml(raw: string): Maybe<string> {
  const text = raw.replace(/\r\n?/g, "\n");
  if (text.startsWith("---\n") === false) return nothing;
  const end = text.indexOf("\n---\n", 3);
  return end === -1 ? nothing : just(text.slice(4, end));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    Array.isArray(value) === false
  );
}

function stringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((v): v is string => typeof v === "string")
    : [];
}

/** A Database link / arc target renamed with its file. */
export function renamedTarget(file: string): string {
  return isLegacyDocumentPath(file) ? zwdocPathFor(file) : file;
}

/**
 * Map Database entry YAML onto `ZwDocEntityFields`, mirroring the serde
 * struct in `src-tauri/src/entity_frontmatter.rs` (unknown keys dropped,
 * `beat` accepted for `moment`, empty values omitted). Link and arc targets
 * point at the converted `.zwdoc` names.
 */
export function entityFieldsFromYaml(yaml: unknown): Maybe<ZwDocEntityFields> {
  if (isRecord(yaml) === false) return nothing;
  const out: ZwDocEntityFields = {};
  for (const key of [
    "type",
    "role",
    "plural",
    "adjective",
    "summary",
    "image",
    "color",
    "status",
  ] as const) {
    const value = yaml[key];
    if (typeof value === "string") out[key] = value;
  }
  const aka = stringList(yaml.aka);
  if (aka.length > 0) out.aka = aka;
  const tags = stringList(yaml.tags);
  if (tags.length > 0) out.tags = tags;
  if (isRecord(yaml.custom)) {
    const custom: Record<string, string | string[]> = {};
    for (const [key, value] of Object.entries(yaml.custom)) {
      if (typeof value === "string") custom[key] = value;
      else if (Array.isArray(value)) custom[key] = stringList(value);
    }
    if (Object.keys(custom).length > 0) out.custom = custom;
  }
  if (Array.isArray(yaml.links)) {
    const links = yaml.links.filter(isRecord).flatMap((link) =>
      typeof link.id === "string" &&
      typeof link.file === "string" &&
      typeof link.relation === "string"
        ? [
            {
              id: link.id,
              file: renamedTarget(link.file),
              relation: link.relation,
              ...(typeof link.note === "string" ? { note: link.note } : {}),
            },
          ]
        : [],
    );
    if (links.length > 0) out.links = links;
  }
  if (Array.isArray(yaml.arc)) {
    const arc = yaml.arc.filter(isRecord).flatMap((moment) => {
      const text =
        typeof moment.moment === "string" ? moment.moment : moment.beat;
      if (typeof moment.id !== "string" || typeof text !== "string") return [];
      if (Number.isInteger(moment.order) === false) return [];
      return [
        {
          id: moment.id,
          order: Number(moment.order),
          ...(typeof moment.at === "string" ? { at: moment.at } : {}),
          moment: text,
          ...(isRecord(moment.link) && typeof moment.link.file === "string"
            ? { link: { file: renamedTarget(moment.link.file) } }
            : {}),
        },
      ];
    });
    if (arc.length > 0) out.arc = arc;
  }
  return Object.keys(out).length > 0 ? just(out) : nothing;
}

const DOC_KINDS: readonly ZwDocKind[] = ["scene", "chapter", "act"];

/**
 * Converter input for one legacy file: body + header values from
 * frontmatter. Database entries carry entity fields; everything else
 * synopsis + park kind.
 */
export function legacyDocument(
  path: string,
  raw: string,
): { input: LegacyMarkdownDocument; warnings: string[] } {
  const warnings: string[] = [];
  const markdown = stripLegacyFrontmatter(raw);
  let yaml: unknown = null;
  const yamlText = frontmatterYaml(raw);
  if (isJust(yamlText)) {
    try {
      yaml = parseYaml(yamlText.value);
    } catch {
      warnings.push(`${path}: unreadable frontmatter, header left empty`);
    }
  }

  if (path.split("/")[0] === "Database") {
    return {
      input: {
        markdown,
        synopsis: "",
        kind: nothing,
        fields: entityFieldsFromYaml(yaml),
      },
      warnings,
    };
  }

  const record = isRecord(yaml) ? yaml : {};
  const kind = DOC_KINDS.find((k) => k === record.kind);
  return {
    input: {
      markdown,
      synopsis: typeof record.synopsis === "string" ? record.synopsis : "",
      kind: typeof kind === "undefined" ? nothing : just(kind),
      fields: nothing,
    },
    warnings,
  };
}

// --- Word check --------------------------------------------------------------

/** Words in rendered Markdown (same dialect as the converter). */
function renderedWords(md: string): string[] {
  const html = String(markdownLexer.parse(md, { async: false }));
  return html
    .replace(
      /<\/(p|h[1-6]|li|blockquote|td|th|tr|pre|div)>|<(br|hr)\s*\/?>/gi,
      " ",
    )
    .replace(/<[^>]+>/g, "")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .split(/\s+/)
    .filter((word) => word !== "");
}

/** Source prose minus editor chrome (comment markers, store, wrapper lines). */
function referenceMarkdown(body: string): string {
  return removeStoreBlock(body)
    .split("\n")
    .filter(
      (line) =>
        isAlignMarkerLine(line) === false &&
        isIndentMarkerLine(line) === false &&
        isSpacerMarkerLine(line) === false,
    )
    .map(stripAllCommentMarkers)
    .join("\n");
}

export type WordCheck = {
  source: number;
  kept: number;
  missing: number;
  extra: number;
};

/**
 * Words in the source vs words the `.zwdoc` keeps (plain-text projection
 * plus raw blocks). Order-insensitive multiset compare.
 */
export function compareWords(body: string, zwdoc: ZwDoc): WordCheck {
  const kept = projectPlainText(zwdoc.doc)
    .text.split(/\s+/)
    .filter((word) => word !== "");
  zwdoc.doc.descendants((node) => {
    if (node.type.name === "raw") {
      kept.push(...renderedWords(String(node.attrs.markdown)));
    }
    return true;
  });
  const source = renderedWords(referenceMarkdown(body));
  const balance = new Map<string, number>();
  for (const word of source) balance.set(word, (balance.get(word) ?? 0) + 1);
  for (const word of kept) balance.set(word, (balance.get(word) ?? 0) - 1);
  let missing = 0;
  let extra = 0;
  for (const count of balance.values()) {
    if (count > 0) missing += count;
    if (count < 0) extra -= count;
  }
  return { source: source.length, kept: kept.length, missing, extra };
}

// --- One document ------------------------------------------------------------

export type ConvertedDocument = {
  path: string;
  target: string;
  content: string;
  words: WordCheck;
  warnings: string[];
};

/** Convert one legacy file; Err explains why it can't be converted safely. */
export function convertLegacyDocument(
  path: string,
  raw: string,
): Result<ConvertedDocument, string> {
  const { input, warnings } = legacyDocument(path, raw);
  const converted = markdownToZwDoc(input);
  if (converted.ok === false) {
    return err(
      `line ${String(converted.error.line)}: ${converted.error.message}`,
    );
  }
  const content = serializeZwDoc(converted.value);
  const reparsed = parseZwDoc(content);
  if (reparsed.ok === false || serializeZwDoc(reparsed.value) !== content) {
    return err("the converted document does not read back the same");
  }
  const words = compareWords(input.markdown, converted.value);
  if (words.missing !== 0 || words.extra !== 0) {
    return err(
      `${String(words.missing)} words missing / ${String(words.extra)} extra of ${String(words.source)}`,
    );
  }
  return ok({ path, target: zwdocPathFor(path), content, words, warnings });
}

// --- References --------------------------------------------------------------

/** Rename document references (`*.md` → `*.zwdoc`) in `.zwconfig` JSON. */
export function renameConfigReferences(value: unknown): unknown {
  if (typeof value === "string") return renamedTarget(value);
  if (Array.isArray(value)) return value.map(renameConfigReferences);
  if (isRecord(value)) {
    const out: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(value)) {
      // Book metadata is the author's text, not file names.
      out[key] = key === "metadata" ? child : renameConfigReferences(child);
    }
    return out;
  }
  return value;
}

/** `.zwgraph` positions are keyed by Database entry file name. */
function renameGraphKeys(value: unknown): unknown {
  if (isRecord(value) === false) return value;
  const out: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(value)) {
    out[key] =
      isRecord(child) && key === "nodes"
        ? Object.fromEntries(
            Object.entries(child).map(([name, pos]) => [
              renamedTarget(name),
              pos,
            ]),
          )
        : child;
  }
  return out;
}

function rewriteJson(
  text: string,
  rename: (value: unknown) => unknown,
): Result<string, string> {
  try {
    const before: unknown = JSON.parse(text);
    const after = rename(before);
    if (JSON.stringify(after) === JSON.stringify(before)) return ok(text);
    return ok(`${JSON.stringify(after, null, 2)}\n`);
  } catch (error) {
    return err(error instanceof Error ? error.message : String(error));
  }
}

// --- Whole project -----------------------------------------------------------

export type ProjectFile = { path: string; content: string };

export type ConversionPlan = {
  /** New `.zwdoc` files and rewritten `.zwconfig` / `.zwgraph`. */
  writes: ProjectFile[];
  /** Legacy documents to delete (each has a `.zwdoc` in `writes`). */
  removes: string[];
  warnings: string[];
};

export type ConversionFailure = { path: string; reason: string };

/**
 * Plan converting every legacy document. `existing` are paths already in
 * the project (to catch `x.md` next to an existing `x.zwdoc`). Nothing is
 * planned unless every document converts cleanly.
 */
export function planProjectConversion(input: {
  documents: readonly ProjectFile[];
  existing: readonly string[];
  zwconfig: Maybe<string>;
  zwgraph: Maybe<string>;
}): Result<ConversionPlan, ConversionFailure[]> {
  const failures: ConversionFailure[] = [];
  const writes: ProjectFile[] = [];
  const warnings: string[] = [];
  const taken = new Map<string, string>();
  const existing = new Set(input.existing.map((p) => p.toLowerCase()));

  for (const doc of input.documents) {
    if (isLegacyDocumentPath(doc.path) === false) continue;
    const target = zwdocPathFor(doc.path);
    const key = target.toLowerCase();
    const clash = taken.get(key);
    if (typeof clash !== "undefined") {
      failures.push({ path: doc.path, reason: `same name as ${clash}` });
      continue;
    }
    if (existing.has(key)) {
      failures.push({ path: doc.path, reason: `${target} already exists` });
      continue;
    }
    taken.set(key, doc.path);
    const converted = convertLegacyDocument(doc.path, doc.content);
    if (converted.ok === false) {
      failures.push({ path: doc.path, reason: converted.error });
      continue;
    }
    writes.push({ path: target, content: converted.value.content });
    warnings.push(...converted.value.warnings);
  }

  for (const [path, text, rename] of [
    [".zwconfig", input.zwconfig, renameConfigReferences],
    ["Database/.zwgraph", input.zwgraph, renameGraphKeys],
  ] as const) {
    if (isJust(text) === false) continue;
    const rewritten = rewriteJson(text.value, rename);
    if (rewritten.ok === false) {
      failures.push({ path, reason: rewritten.error });
    } else if (rewritten.value !== text.value) {
      writes.push({ path, content: rewritten.value });
    }
  }

  if (failures.length > 0) return err(failures);
  return ok({
    writes,
    removes: input.documents
      .map((doc) => doc.path)
      .filter(isLegacyDocumentPath),
    warnings,
  });
}
